import React, { useContext, useState } from 'react';
import { StaticContext } from '@sm/webassets/StaticContext';
import { generateMetricsAttribute } from '@sm/metrics';
import { createURL } from '@sm/utils';
import { Button } from '@wds/button';
import { IconArrowRight } from '@wds/icons';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { getSrc } from '~app/helpers/assetLoading';
import { treatment2Create, treatment2Take } from '~static/images/optionalPaths';
import useStyles from './useStyles';
import ColoredLogoWithText from '~app/components/surveyThanksLogo/ColoredLogoWithText';
import { MetricsProps, OptionalPathsCopyType } from '~helpers/types';
import HeroFormForOP from './components/HeroFormForOP';
import { CONTRIBUTE_SIGNUP_URL } from '~app/components/constants';

const COPY: OptionalPathsCopyType = {
  banner: 'Thank you for taking this survey.',
  header: 'Discover what else you can do with SurveyMonkey',
  creatorAlt: 'Survey creator',
  creatorHeader: 'Create unlimited forms and surveys for free',
  creatorSubHeader:
    'Send surveys to anyone, from friends to customers. Use insights to make better, data-driven decisions.',
  creatorCTA: 'Create surveys',
  takerAlt: 'Survey taker',
  takerHeader: 'Take more surveys and help charities',
  takerSubHeader:
    'With each survey, we’ll donate $0.50 to a participating charity. You could also win $100 gift cards.',
  takerCTA: 'Take more surveys',
};

function OptionalPathsV2({ utSource2 = 'OptionalPaths' }: { utSource2: string }): React.ReactElement {
  const utSource = 'survey-thanks';
  const utSource3SignUp = 'DesktopSignUp';
  const utSource3Contribute = 'DesktopContribute';
  const [createSurveyClicked, setCreateSurveyClicked] = useState(false);
  const [signUpAppeared, setSignUPAppeared] = useState(false);

  const {
    smParams: { surveyId, collectorId, respondentId },
    templateType,
  } = useContext(StaticContext);

  const metricsProps: MetricsProps = {
    amplitudeEvent: 'optional paths v2 signup',
    surveyId,
    collectorId,
    respondentId,
    utSource,
    utSource2,
    templateType,
    utSource3: utSource3SignUp,
  };

  const {
    card,
    headerText,
    leftContent,
    rightContent,
    pathImage,
    pageContent,
    fadeOut,
    hideLogoWithText,
    logoWithText,
    header,
    hide,
    zIndexOverlay,
    ctaFormWrapper,
  } = useStyles();

  const handleCreateSurvey = (): void => {
    setCreateSurveyClicked(true);
    setSignUPAppeared(true);
  };

  return (
    <>
      <Box className={`${logoWithText} ${signUpAppeared ? hideLogoWithText : ''}`}>
        <ColoredLogoWithText
          isThanksText
          utSource={utSource}
          utSource2={utSource2}
          amplitudeEvent="endpage user interaction"
        />
      </Box>
      <Box className={pageContent}>
        <Box className={`${header} ${signUpAppeared ? hide : ''}`}>
          <Typography variant="pageTitle" weight="medium" component="h1" align="center">
            {COPY.header}
          </Typography>
        </Box>
        <Box display="flex" gap={10} justifyContent="center">
          <Box className={`${ctaFormWrapper} ${leftContent} ${!createSurveyClicked ? fadeOut : undefined}`}>
            <HeroFormForOP metricsProps={metricsProps} />
          </Box>
          <Box display="flex" className={`${zIndexOverlay} ${createSurveyClicked ? rightContent : leftContent}`}>
            <Box className={card} display="flex" flexDirection="column">
              <img src={getSrc(treatment2Create)} alt={COPY.creatorAlt} className={pathImage} />
              <Box pt={6} pb={5}>
                <Box pb={5}>
                  <Box pb={4} className={headerText}>
                    <Typography variant="cardTitle" weight="medium" component="h2">
                      {COPY.creatorHeader}
                    </Typography>
                  </Box>
                  <Typography component="p">{COPY.creatorSubHeader}</Typography>
                </Box>
                <Box className={createSurveyClicked ? fadeOut : ''}>
                  <Button
                    onClick={handleCreateSurvey}
                    data-sm-metrics={generateMetricsAttribute({
                      data: {
                        amplitudeEvent: 'optional paths v2 survey creator cta',
                        itemSelected: 'button',
                        templateType,
                        surveyId,
                        collectorId,
                        respondentId,
                        log: {
                          message: `${utSource}-${utSource2}-${utSource3SignUp}`,
                        },
                      },
                    })}
                  >
                    {COPY.creatorCTA}
                    <Box display="inline" ml={4}>
                      <IconArrowRight title="arrow icon" />
                    </Box>
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box display="flex" className={`${zIndexOverlay} ${rightContent} ${createSurveyClicked ? fadeOut : ''}`}>
            <Box className={card} display="flex" flexDirection="column">
              <img src={getSrc(treatment2Take)} alt={COPY.takerAlt} className={pathImage} />
              <Box pt={6} pb={5}>
                <Box pb={5}>
                  <Box pb={4} className={headerText}>
                    <Typography variant="cardTitle" weight="medium" component="h2">
                      {COPY.takerHeader}
                    </Typography>
                  </Box>
                  <Typography component="p">{COPY.takerSubHeader}</Typography>
                </Box>
                <Box>
                  <Button
                    color="alt"
                    href={createURL(CONTRIBUTE_SIGNUP_URL, {
                      ut_source: utSource,
                      ut_source2: utSource2,
                      ut_source3: utSource3Contribute,
                      ut_ctatext: COPY.takerCTA,
                      fsub: 'true',
                    })}
                    data-sm-metrics={generateMetricsAttribute({
                      data: {
                        amplitudeEvent: 'optional paths v2 contribute cta',
                        itemSelected: 'button',
                        templateType,
                        surveyId,
                        collectorId,
                        respondentId,
                        log: {
                          message: `${utSource}-${utSource2}-${utSource3Contribute}`,
                        },
                      },
                    })}
                  >
                    {COPY.takerCTA}
                    <Box display="inline" ml={4}>
                      <IconArrowRight title="arrow icon" />
                    </Box>
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default OptionalPathsV2;
